import React, { useState, useEffect, useRef } from 'react';
import './SplashScreen.css';

export default function SplashScreen({ ready, onFinish }) {
  const [fadeOut, setFadeOut] = useState(false);
  const starsRef = useRef(null);
  const loaderRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    if (!ready) return;
    const t = setTimeout(() => setFadeOut(true), 2000);
    return () => clearTimeout(t);
  }, [ready]);

  const handleAnimationEnd = () => {
    if (fadeOut) onFinish();
  };

  // Stars canvas
  useEffect(() => {
    const cv = starsRef.current;
    if (!cv) return;
    const ctx = cv.getContext('2d');
    let raf;

    function resize() {
      cv.width = cv.offsetWidth;
      cv.height = cv.offsetHeight;
    }
    resize();
    window.addEventListener('resize', resize);

    const W = () => cv.width, H = () => cv.height;
    function rand(a, b) { return a + Math.random() * (b - a); }

    const BG = [];
    for (let i = 0; i < 180; i++) BG.push({ x: rand(0, 400), y: rand(0, 900), r: rand(0.2, 0.9), a: rand(0.06, 0.28), phase: rand(0, Math.PI * 2), spd: rand(0.2, 0.7), vx: rand(-0.012, 0.012), vy: rand(-0.006, 0.006) });
    const BRIGHT = [];
    for (let i = 0; i < 20; i++) BRIGHT.push({ x: rand(0, 400), y: rand(0, 900), r: rand(0.8, 1.8), a: rand(0.4, 0.85), phase: rand(0, Math.PI * 2), spd: rand(0.3, 1), pulseAmp: rand(0.15, 0.35), vx: rand(-0.008, 0.008), vy: rand(-0.005, 0.005), hue: Math.random() < 0.4 ? rand(195, 230) : 0 });
    const MICRO = [];
    for (let i = 0; i < 55; i++) MICRO.push({ x: rand(0, 400), y: rand(0, 900), r: rand(0.15, 0.4), a: rand(0.05, 0.18), phase: rand(0, Math.PI * 2), spd: rand(1.5, 4) });
    const SHOOTS = []; let nextShoot = 120;
    function makeShoot() { const w = W(), h = H(); const ang = rand(10, 25) * Math.PI / 180, spd = rand(6, 11); return { x: rand(10, w * 0.75), y: rand(0, h * 0.45), vx: Math.cos(ang) * spd, vy: Math.sin(ang) * spd, len: rand(70, 130), life: 0, maxLife: rand(50, 80), a: 0 }; }
    const FLARES = []; let nextFlare = 180;
    function makeFlare() { const w = W(), h = H(); return { x: rand(20, w - 20), y: rand(20, h * 0.8), r: 0, maxR: rand(6, 14), life: 0, maxLife: 50, a: 0 }; }

    function draw(t) {
      const w = W(), h = H();
      ctx.clearRect(0, 0, w, h);
      BG.forEach(s => { s.x += s.vx; s.y += s.vy; if (s.x < -2) s.x = w + 2; if (s.x > w + 2) s.x = -2; if (s.y < -2) s.y = h + 2; if (s.y > h + 2) s.y = -2; const a = Math.max(0, s.a + Math.sin(t * 0.001 * s.spd + s.phase) * 0.08); ctx.beginPath(); ctx.arc(s.x, s.y, s.r, 0, Math.PI * 2); ctx.fillStyle = `rgba(200,220,255,${a})`; ctx.fill(); });
      MICRO.forEach(s => { const a = Math.max(0, s.a + Math.sin(t * 0.001 * s.spd + s.phase) * s.a * 0.9); ctx.beginPath(); ctx.arc(s.x, s.y, s.r, 0, Math.PI * 2); ctx.fillStyle = `rgba(220,235,255,${a})`; ctx.fill(); });
      BRIGHT.forEach(s => { s.x += s.vx; s.y += s.vy; if (s.x < -2) s.x = w + 2; if (s.x > w + 2) s.x = -2; if (s.y < -2) s.y = h + 2; if (s.y > h + 2) s.y = -2; const pulse = Math.sin(t * 0.001 * s.spd + s.phase) * s.pulseAmp; const a = Math.max(0, Math.min(1, s.a + pulse)); const r = s.r * (1 + pulse * 0.3); ctx.save(); ctx.beginPath(); ctx.arc(s.x, s.y, r, 0, Math.PI * 2); ctx.fillStyle = s.hue > 0 ? `hsla(${s.hue},70%,88%,${a})` : `rgba(240,248,255,${a})`; ctx.shadowColor = s.hue > 0 ? `hsla(${s.hue},80%,80%,0.7)` : `rgba(180,215,255,0.65)`; ctx.shadowBlur = 6 + pulse * 10; ctx.fill(); ctx.restore(); });
      nextFlare--; if (nextFlare <= 0) { FLARES.push(makeFlare()); nextFlare = rand(120, 350); }
      for (let i = FLARES.length - 1; i >= 0; i--) { const f = FLARES[i]; f.life++; const tp = f.life / f.maxLife; f.r = f.maxR * Math.sin(tp * Math.PI); f.a = Math.sin(tp * Math.PI) * 0.5; if (f.life >= f.maxLife) { FLARES.splice(i, 1); continue; } const fg = ctx.createRadialGradient(f.x, f.y, 0, f.x, f.y, f.r); fg.addColorStop(0, `rgba(255,255,255,${f.a})`); fg.addColorStop(1, `rgba(150,200,255,0)`); ctx.beginPath(); ctx.arc(f.x, f.y, f.r, 0, Math.PI * 2); ctx.fillStyle = fg; ctx.fill(); }
      nextShoot--; if (nextShoot <= 0) { SHOOTS.push(makeShoot()); nextShoot = rand(160, 360); }
      for (let i = SHOOTS.length - 1; i >= 0; i--) { const s = SHOOTS[i]; s.life++; s.x += s.vx; s.y += s.vy; if (s.life < 8) s.a = s.life / 8; else if (s.life > s.maxLife - 12) s.a = Math.max(0, (s.maxLife - s.life) / 12); else s.a = 1; if (s.life >= s.maxLife || s.x > w + 20 || s.y > h + 20) { SHOOTS.splice(i, 1); continue; } const mag = Math.hypot(s.vx, s.vy); const tx = s.x - s.vx / mag * s.len, ty = s.y - s.vy / mag * s.len; const sg = ctx.createLinearGradient(tx, ty, s.x, s.y); sg.addColorStop(0, 'rgba(160,210,255,0)'); sg.addColorStop(1, `rgba(255,255,255,${s.a})`); ctx.save(); ctx.beginPath(); ctx.moveTo(tx, ty); ctx.lineTo(s.x, s.y); ctx.strokeStyle = sg; ctx.lineWidth = 1.4; ctx.shadowColor = 'rgba(150,210,255,0.9)'; ctx.shadowBlur = 8; ctx.stroke(); ctx.beginPath(); ctx.arc(s.x, s.y, 1.4, 0, Math.PI * 2); ctx.fillStyle = `rgba(255,255,255,${s.a})`; ctx.shadowBlur = 12; ctx.fill(); ctx.restore(); }
      raf = requestAnimationFrame(draw);
    }
    raf = requestAnimationFrame(draw);
    return () => { cancelAnimationFrame(raf); window.removeEventListener('resize', resize); };
  }, []);

  // Loader canvas
  useEffect(() => {
    const cv = loaderRef.current;
    if (!cv) return;
    const ctx = cv.getContext('2d');
    let raf;

    function resize() {
      cv.width = cv.offsetWidth;
      cv.height = cv.offsetHeight;
    }
    resize();
    window.addEventListener('resize', resize);

    const LOOP = 2800;
    const startT = performance.now();
    const sparks = [];
    function spawnSpark(x, y) {
      sparks.push({ x, y, vx: (Math.random() - 0.5) * 2.5, vy: -Math.random() * 2.5 - 0.5, life: 0, maxLife: 20 + Math.random() * 20, r: 0.6 + Math.random() * 1.2 });
    }

    function draw(now) {
      const W = cv.width, H = cv.height;
      ctx.clearRect(0, 0, W, H);
      const BW = Math.min(260, W * 0.7), BH = 3, BX = (W - BW) / 2, BY = H - 80;
      const elapsed = (now - startT) % LOOP;
      const progress = elapsed / LOOP;
      const fillW = BW * progress;
      const ex = BX + fillW;

      // track
      ctx.save();
      ctx.strokeStyle = 'rgba(255,255,255,0.06)';
      ctx.lineWidth = BH;
      ctx.lineCap = 'round';
      ctx.beginPath(); ctx.moveTo(BX, BY); ctx.lineTo(BX + BW, BY);
      ctx.stroke();
      ctx.restore();

      // filled portion
      if (fillW > 2) {
        const g = ctx.createLinearGradient(BX, 0, ex, 0);
        g.addColorStop(0, 'rgba(20,200,180,0.6)');
        g.addColorStop(0.4, 'rgba(100,80,220,0.9)');
        g.addColorStop(0.8, 'rgba(200,80,180,0.95)');
        g.addColorStop(1, 'rgba(255,255,255,1)');
        ctx.save();
        ctx.strokeStyle = g;
        ctx.lineWidth = BH;
        ctx.lineCap = 'round';
        ctx.shadowColor = 'rgba(180,100,255,0.8)';
        ctx.shadowBlur = 10;
        ctx.beginPath(); ctx.moveTo(BX, BY); ctx.lineTo(ex, BY);
        ctx.stroke();
        ctx.restore();

        // glow bloom
        const bloom = ctx.createRadialGradient(ex, BY, 0, ex, BY, 22);
        bloom.addColorStop(0, 'rgba(255,255,255,0.5)');
        bloom.addColorStop(0.3, 'rgba(180,100,255,0.25)');
        bloom.addColorStop(1, 'rgba(0,0,0,0)');
        ctx.save();
        ctx.fillStyle = bloom;
        ctx.beginPath(); ctx.arc(ex, BY, 22, 0, Math.PI * 2); ctx.fill();
        ctx.restore();

        // bright head dot
        ctx.save();
        ctx.fillStyle = '#fff';
        ctx.shadowColor = 'rgba(220,180,255,1)';
        ctx.shadowBlur = 20;
        ctx.beginPath(); ctx.arc(ex, BY, 3, 0, Math.PI * 2); ctx.fill();
        ctx.restore();

        if (Math.random() < 0.55) spawnSpark(ex, BY);

        // energy pulse nodes
        for (let i = 1; i <= 4; i++) {
          const nx = ex - i * fillW * 0.18;
          if (nx < BX) break;
          const pulse = Math.sin(now * 0.005 - i * 1.2) * 0.5 + 0.5;
          ctx.save();
          ctx.fillStyle = `rgba(160,100,255,${0.15 + pulse * 0.25})`;
          ctx.shadowColor = 'rgba(160,100,255,0.6)';
          ctx.shadowBlur = 8 * pulse;
          ctx.beginPath(); ctx.arc(nx, BY, 1 + pulse * 1.2, 0, Math.PI * 2); ctx.fill();
          ctx.restore();
        }
      }

      // sparks
      for (let i = sparks.length - 1; i >= 0; i--) {
        const s = sparks[i];
        s.life++; s.x += s.vx; s.y += s.vy; s.vy += 0.12;
        const a = 1 - s.life / s.maxLife;
        if (a <= 0) { sparks.splice(i, 1); continue; }
        ctx.save();
        ctx.globalAlpha = a * 0.8;
        ctx.fillStyle = 'rgba(220,180,255,1)';
        ctx.shadowColor = 'rgba(220,180,255,0.9)';
        ctx.shadowBlur = 6;
        ctx.beginPath(); ctx.arc(s.x, s.y, s.r, 0, Math.PI * 2); ctx.fill();
        ctx.restore();
      }

      // percentage
      ctx.save();
      ctx.font = '500 10px Inter,sans-serif';
      ctx.textAlign = 'center';
      ctx.fillStyle = 'rgba(255,255,255,0.35)';
      ctx.fillText(Math.round(progress * 100) + '%', W / 2, BY + 18);
      ctx.restore();

      raf = requestAnimationFrame(draw);
    }
    raf = requestAnimationFrame(draw);
    return () => { cancelAnimationFrame(raf); window.removeEventListener('resize', resize); };
  }, []);

  return (
    <div
      ref={containerRef}
      className={`splash-screen${fadeOut ? ' fade-out' : ''}`}
      onAnimationEnd={handleAnimationEnd}
    >
      <canvas ref={starsRef} className="splash-canvas splash-stars" />
      <canvas ref={loaderRef} className="splash-canvas splash-loader" />
      <img
        className="splash-hero"
        src="/splash-hero.png"
        alt="Sutrum AI"
      />
      <div className="splash-brand">
        <div className="splash-brand-name">Sutrum AI</div>
      </div>
    </div>
  );
}
